import {
  Avatar,
  ButtonDS,
  DialogModalDS,
  DirectionsCarFilledOutlined,
  Spacer,
  TableDS,
  TextCapitalized,
  triggerToastDS,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import type { TableHeaderProps } from "@qivia/ui/src/designSystem/components/table/TableHeader";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { formatCurrency } from "../../../libSlice/materialUiFormats";
import { ElectricCharge } from "./electricChargingAPI";
import {
  electricChargingSlice,
  reimburseChargesAsync,
  selectElectricCharges,
  selectReimburseChargesStatus,
} from "./electricChargingSlice";
import "../../../i18n/i18n";

type ReimburseChargeDisplayed = Pick<
  ElectricCharge,
  "registrationNumber" | "driverName" | "refundAmount"
>;

export const ReimburseChargesModal = (props: {
  isVisible: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isVisible, onClose } = props;
  const charges = useAppSelector(selectElectricCharges);
  const reimburseStatus = useAppSelector(selectReimburseChargesStatus);
  const [rowsChecked, setRowsChecked] = useState<ElectricCharge[]>([]);

  const toRefundCharges = useMemo(
    () => charges.filter((charge) => charge.refundStatus === "TO_REFUND"),
    [charges],
  );

  const total = useMemo(
    () =>
      rowsChecked.reduce((sum, charge) => sum + (charge.refundAmount ?? 0), 0),
    [rowsChecked],
  );

  const onSubmit = useCallback(() => {
    if (rowsChecked.length === 0) {
      return;
    }
    void dispatch(
      reimburseChargesAsync(rowsChecked.map((charge) => charge.uuid)),
    );
  }, [dispatch, rowsChecked]);

  useEffect(() => {
    if (reimburseStatus === "success") {
      triggerToastDS(t("electric_charging.reimburseModal.success"), "SUCCESS");
      dispatch(electricChargingSlice.actions.resetReimburseChargesStatus());
      setRowsChecked([]);
      onClose();
    }
    if (reimburseStatus === "failed") {
      triggerToastDS(t("electric_charging.reimburseModal.error"), "ERROR");
      dispatch(electricChargingSlice.actions.resetReimburseChargesStatus());
    }
  }, [reimburseStatus, t, dispatch, onClose]);

  const headers = {
    registrationNumber: { text: "" },
    driverName: { text: "" },
    refundAmount: { text: "" },
  };

  const render =
    (row: ElectricCharge) => (key: keyof ReimburseChargeDisplayed) => {
      switch (key) {
        case "registrationNumber":
          return (
            <StyledRow>
              <StyledCarIcon>
                <DirectionsCarFilledOutlined />
              </StyledCarIcon>
              <Spacer x={0.5} />
              {row.registrationNumber}
            </StyledRow>
          );
        case "driverName": {
          if (!row.driverName) {
            return "-";
          }
          const initials = row.driverName
            .split(" ")
            .map((word) => word.charAt(0));
          return (
            <StyledRow>
              <Avatar
                color={"mint"}
                size={"S"}
                firstName={initials[0]}
                lastName={initials[initials.length - 1]}
              />
              <Spacer x={0.75} />
              {row.driverName}
            </StyledRow>
          );
        }
        case "refundAmount":
          return (
            <StyledAmount>{formatCurrency(row.refundAmount ?? 0)}</StyledAmount>
          );
      }
    };

  if (!isVisible) {
    return null;
  }

  return (
    <DialogModalDS
      onClose={onClose}
      visible={isVisible}
      width={38}
      title={t("electric_charging.reimburseModal.title")}
      bottomButtons={{
        leftButton: (
          <ButtonDS
            text={t("electric_charging.reimburseModal.cancel")}
            format={"fill"}
            buttonType={"secondary"}
            onClick={onClose}
          />
        ),
        rightButton: (
          <ButtonDS
            text={t("electric_charging.reimburseModal.validate")}
            format={"fill"}
            buttonType={"primary"}
            onClick={onSubmit}
            disabled={
              rowsChecked.length === 0 || reimburseStatus === "processing"
            }
          />
        ),
      }}
    >
      <TableDS<keyof ReimburseChargeDisplayed, ElectricCharge>
        data={toRefundCharges}
        headers={headers as Record<keyof ElectricCharge, TableHeaderProps>}
        render={render}
        rowsChecked={rowsChecked}
        setRowsChecked={setRowsChecked}
        width={35}
        height={25}
        header-checkbox-data-cy="header-checkbox-data-cy"
        headerOverlayed={
          <StyledHeader>
            <TextCapitalized>
              {t("electric_charging.reimburseModal.selected", {
                count: rowsChecked.length,
              })}
            </TextCapitalized>
            <StyledRow>
              <TextCapitalized>
                {t("electric_charging.reimburseModal.total")}
              </TextCapitalized>
              <Spacer x={0.5} />
              <StyledAmount>{formatCurrency(total)}</StyledAmount>
            </StyledRow>
          </StyledHeader>
        }
      />
    </DialogModalDS>
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: center;
`;

const StyledCarIcon = styled.div`
  display: flex;
  width: 1.25rem;
  height: 1.25rem;
  & svg {
    width: 100%;
    height: 100%;
  }
`;

const StyledAmount = styled.div`
  font-weight: 700;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 30rem;
  ${typographies["Body/M"]};
  color: ${colors["colors/text/black"]};
`;
